/**
 * CSV Export Utility
 * Provides functions to export data to CSV format
 */

export interface CSVColumn {
  key: string;
  header: string;
  formatter?: (value: any) => string;
}

/**
 * Convert array of objects to CSV string
 */
export function convertToCSV(data: any[], columns: CSVColumn[]): string {
  if (!data || data.length === 0) {
    return '';
  }

  // Create header row
  const headers = columns.map(col => col.header);
  const csvRows = [headers.join(',')];

  // Create data rows
  data.forEach(row => {
    const values = columns.map(col => {
      let value = getNestedValue(row, col.key);
      
      // Apply formatter if provided
      if (col.formatter && value !== null && value !== undefined) {
        value = col.formatter(value);
      }
      
      // Handle null/undefined values
      if (value === null || value === undefined) {
        return '';
      }
      
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      
      return stringValue;
    });
    
    csvRows.push(values.join(','));
  });

  return csvRows.join('\n');
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Get nested object value using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
}

/**
 * Format currency amount for CSV
 */
export function formatCurrencyForCSV(currencyObj: { currency: string; amount: number }): string {
  if (!currencyObj || !currencyObj.currency || currencyObj.amount === undefined) {
    return '';
  }
  return `${currencyObj.amount} ${currencyObj.currency}`;
}

/**
 * Format date for CSV (timezone-aware)
 */
export function formatDateForCSV(dateString: string, timezone?: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    if (timezone) {
      options.timeZone = timezone;
    }
    
    return date.toLocaleDateString('en-US', options);
  } catch {
    return dateString;
  }
}
